import { computed, observable, action, runInAction } from 'mobx';
import { message } from 'antd';
import type { HomeShortcut, HomeShortcutFormValues } from '../types/HomeShortcut';
import { HomeShortcutsService } from '../service/HomeShortcutsService';

const MAX_CUSTOM_SHORTCUTS = 5;

export class HomeShortcutsStore {
    @observable
    shortcuts: HomeShortcut[] = [];

    @observable
    isLoading: boolean = false;

    @observable
    isSaving: boolean = false;

    @observable
    isReordering: boolean = false;

    @observable
    isEditModalVisible: boolean = false;

    @observable
    editingShortcut: HomeShortcut | null = null;

    constructor(private readonly service: HomeShortcutsService) {}

    @computed
    get customCards(): HomeShortcut[] {
        return this.shortcuts
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
    }

    @computed
    get canAddMore(): boolean {
        return this.customCards.length < MAX_CUSTOM_SHORTCUTS;
    }

    @action.bound
    initialize() {
        this.loadShortcuts();
    }

    @action.bound
    dispose() {
        this.shortcuts = [];
        this.isLoading = false;
        this.isSaving = false;
        this.isReordering = false;
        this.isEditModalVisible = false;
        this.editingShortcut = null;
    }

    @action.bound
    openCreateModal() {
        this.editingShortcut = null;
        this.isEditModalVisible = true;
    }

    @action.bound
    openEditModal(shortcut: HomeShortcut) {
        this.editingShortcut = shortcut;
        this.isEditModalVisible = true;
    }

    @action.bound
    closeEditModal() {
        this.isEditModalVisible = false;
        this.editingShortcut = null;
    }

    @action.bound
    async loadShortcuts() {
        this.isLoading = true;

        try {
            const response = await this.service.getShortcuts();

            if (response.isErr()) {
                this.showError('load home shortcuts', response.error);
                return;
            }

            runInAction(() => {
                this.shortcuts = response.value;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @action.bound
    async createShortcut(values: HomeShortcutFormValues) {
        if (!this.canAddMore) {
            message.warning(`You can add up to ${MAX_CUSTOM_SHORTCUTS} custom shortcuts.`);
            return false;
        }

        this.isSaving = true;

        try {
            const response = await this.service.createShortcut(this.buildFormData(values));

            if (response.isErr()) {
                this.showError('create the shortcut', response.error);
                return false;
            }

            message.success('Shortcut created.');
            await this.loadShortcuts();
            runInAction(() => {
                this.closeEditModal();
            });

            return true;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async updateShortcut(values: HomeShortcutFormValues) {
        if (!this.editingShortcut) {
            return false;
        }

        this.isSaving = true;

        try {
            const response = await this.service.updateShortcut(this.editingShortcut.id, this.buildFormData(values));

            if (response.isErr()) {
                this.showError('update the shortcut', response.error);
                return false;
            }

            message.success('Shortcut updated.');
            await this.loadShortcuts();
            runInAction(() => {
                this.closeEditModal();
            });

            return true;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async deleteShortcut(id: string) {
        this.isSaving = true;

        try {
            const response = await this.service.deleteShortcut(id);

            if (response.isErr()) {
                this.showError('delete the shortcut', response.error);
                return;
            }

            message.success('Shortcut deleted.');
            await this.loadShortcuts();
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async reorder(dragIndex: number, dropIndex: number) {
        if (dragIndex === dropIndex || this.isReordering) {
            return;
        }

        const reordered = this.customCards.slice();
        const [draggedShortcut] = reordered.splice(dragIndex, 1);

        if (!draggedShortcut) {
            return;
        }

        reordered.splice(dropIndex, 0, draggedShortcut);

        const previousShortcuts = this.shortcuts.slice();
        const updatedShortcuts = reordered.map((shortcut, index) => ({
            ...shortcut,
            sortOrder: index
        }));

        runInAction(() => {
            this.shortcuts = updatedShortcuts;
            this.isReordering = true;
        });

        try {
            const response = await this.service.reorder(updatedShortcuts.map(shortcut => shortcut.id));

            if (response.isErr()) {
                runInAction(() => {
                    this.shortcuts = previousShortcuts;
                });
                this.showError('reorder shortcuts', response.error);
                return;
            }

            message.success('Shortcuts reordered.');
            await this.loadShortcuts();
        } finally {
            runInAction(() => {
                this.isReordering = false;
            });
        }
    }

    @action.bound
    async toggleVisibility(shortcut: HomeShortcut, isVisible: boolean) {
        const response = await this.service.toggleVisibility(shortcut.id, isVisible);

        if (response.isErr()) {
            this.showError('update shortcut visibility', response.error);
            return;
        }

        runInAction(() => {
            this.shortcuts = this.shortcuts.map(item => (item.id === shortcut.id ? response.value : item));
        });

        message.success(`Shortcut ${isVisible ? 'shown' : 'hidden'}.`);
    }

    private buildFormData(values: HomeShortcutFormValues) {
        const formData = new FormData();
        const title = values.title.trim();
        const description = values.description?.trim() ?? '';
        const url = values.url.trim();

        formData.append('Title', title);
        formData.append('Description', description);
        formData.append('Url', url);

        if (values.image) {
            formData.append('Image', values.image);
        }

        if (values.deleteImage) {
            formData.append('DeleteImage', 'true');
        }

        return formData;
    }

    private showError(errorAction: string, error: { data?: { title?: string }; text: string }) {
        const errorMessage = error.data?.title || error.text || `Failed to ${errorAction}.`;
        message.error(errorMessage);
        console.error(error);
    }
}
